import { useState, useRef, useEffect } from "react";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import { styled, keyframes } from "styled-components";

const pulse = keyframes`
  0% {
    transform: scale(0.95);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.7;
  }
  100% {
    transform: scale(0.95);
    opacity: 1;
  }
`;

const VoiceStyles = styled("div")`
  align-self: baseline;
  
  .listening-icon {
    animation: ${pulse} 1s infinite;
    color: red;
  }
  
  button {
    background: transparent;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border-radius: 50%;
    transition: background-color 0.2s;
    
    &:hover {
      background-color: rgba(0, 0, 0, 0.1);
    }
    
    &.active {
      background-color: rgba(255, 0, 0, 0.1);
    }
  }
`;

export const VoiceChat = ({ sendDataToParent, sendMessageByVoice }) => {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const finalTranscriptRef = useRef("");
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const resetTranscript = () => {
    setTranscript("");
    finalTranscriptRef.current = "";
  };

  const clearSilenceTimeout = () => {
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
    }
  };

  const setSilenceTimeout = () => {
    clearSilenceTimeout();
    silenceTimeoutRef.current = setTimeout(() => {
      sendMessageByVoice(finalTranscriptRef.current);
      stopListening();
    }, 2000);
  };

  const sendTranscript = (text: string) => {
    if (text && text.trim()) {
      sendDataToParent(text.trim());
      resetTranscript();
    }
  };

  const startListening = () => {
    const SpeechRecognition =
      window.SpeechRecognition || (window as any).webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      alert("Speech Recognition is not supported in this browser.");
      return;
    }

    try {
      resetTranscript();
      
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";

      recognition.onstart = () => {
        setListening(true);
      };
      
      recognition.onend = () => {
        setListening(false);
        sendTranscript(finalTranscriptRef.current);
      };

      let interimTranscript = '';
      
      recognition.onresult = (event: SpeechRecognitionEvent) => {
        interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          let transcript = event.results[i][0].transcript;
          
          if (event.results[i].isFinal) {
            if (transcript.toLowerCase().includes("send")) {
              if (transcript.endsWith("send")) {
                transcript = transcript.slice(0, -4).trim();
              }
              finalTranscriptRef.current += transcript + " ";
              sendMessageByVoice(finalTranscriptRef.current);
              resetTranscript();
              stopListening();
              return;
            }
            finalTranscriptRef.current += transcript + " ";
          } else {
            interimTranscript += transcript;
          }
        }
        setSilenceTimeout();
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error("Speech recognition error:", event.error);
        setListening(false);
      };

      recognition.start();
      recognitionRef.current = recognition;
    } catch (error) {
      console.error("Error initializing speech recognition:", error);
      alert("Could not start speech recognition. Please try again.");
    }
  };

  const stopListening = () => {
    clearSilenceTimeout();
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  };

  const toggleListening = (event) => {
    event.preventDefault();
    if (listening) {
      stopListening();
    } else {
      resetTranscript();
      startListening();
    }
  };

  return (
    <VoiceStyles>
      <button
        onClick={toggleListening}
        className={`h-8 w-8 rounded-full transition duration-200 flex items-center justify-center ${listening ? "active" : ""}`}
        title={listening ? "Stop listening" : "Start voice input"}
        aria-label={listening ? "Stop voice input" : "Start voice input"}
      >
        {listening ? (
          <MicIcon className="listening-icon" />
        ) : (
          <MicOffIcon />
        )}
      </button>
    </VoiceStyles>
  );
};

export default VoiceChat;